"use client";

// Reps : le SÉLECTEUR de parcours (façon Duolingo/Brilliant/Dyma).
// Icône du parcours courant + chevron ; le dropdown liste les parcours (avec
// progression) pour switcher. /reps ramène sur le parcours courant.
// Deux emplacements : en tête du rail droit dès lg (inRail, façon Duolingo :
// le chemin commence directement par sa bannière d'unité), en tête de la
// colonne centrale sous lg (le rail n'y existe pas).
import { useState, useRef, useEffect } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import RepsIcon from "@/components/Reps/RepsIcon";

export default function RepsTrackSelector({
    tracks,
    currentCode,
    inRail = false,
}) {
    const t = useTranslations();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const list = Array.isArray(tracks) ? tracks : [];
    // Comparaison par CODE canonique (identique dans toutes les langues) :
    // track.slug est le slug localisé, réservé aux URLs.
    const current =
        list.find((x) => (x.code || x.slug) === currentCode) || list[0] || null;

    useEffect(() => {
        function onDoc(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    if (!current) return null;

    return (
        // z-[45] : le dropdown doit passer AU-DESSUS des bannières d'unité
        // sticky du path (z-30) ET d'un callout de nœud ouvert (z-35), sous
        // le header du site (z-50).
        <div ref={ref} className={`relative z-[45] ${inRail ? "" : "mb-6"}`}>
            {/* Dans le rail : pleine largeur, comme les cartes voisines.
                Dans la colonne centrale : pleine largeur sur téléphone (le
                bouton se calerait sinon sur son contenu, plus étroit que les
                bannières d'unité juste en dessous), largeur du contenu dès sm. */}
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={`flex w-full items-center gap-3 rounded-2xl border border-gray-100 bg-white px-3 py-2 text-left transition-colors hover:border-orange-200 dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-orange-500/30 ${
                    inRail ? "" : "sm:w-auto"
                }`}
            >
                {current.icon ? (
                    <RepsIcon size={40} src={current.icon} />
                ) : (
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-xl text-orange-500 dark:bg-orange-500/15">
                        <SolarIcon name="code-square-bold-duotone" />
                    </span>
                )}
                <span className="min-w-0 flex-1">
                    <span className="block text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        {t("reps_selector_label")}
                    </span>
                    {/* Pas de truncate : un titre long passe à la ligne. */}
                    <span className="block break-words font-semibold text-gray-900 dark:text-white">
                        {current.title}
                    </span>
                </span>
                <SolarIcon
                    name="alt-arrow-down-linear"
                    className={`ml-1 text-lg text-gray-400 transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>

            {open && (
                <div
                    className={`absolute left-0 top-full z-20 mt-2 overflow-hidden rounded-2xl border border-gray-100 bg-white p-1.5 shadow-xl dark:border-white/10 dark:bg-bgDark-840 ${
                        inRail ? "w-full" : "w-72 max-w-[90vw]"
                    }`}
                >
                    {list.map((track) => {
                        const done = track.completedCount || 0;
                        const total = track.lessonsCount || 0;
                        const isCurrent =
                            (track.code || track.slug) ===
                            (current.code || current.slug);
                        return (
                            <Link
                                key={track.code || track.slug}
                                href={{
                                    pathname: "/reps/path/[trackSlug]",
                                    params: { trackSlug: track.slug },
                                }}
                                onClick={() => setOpen(false)}
                                className={`flex items-center gap-3 rounded-xl px-2.5 py-2 transition-colors ${
                                    isCurrent
                                        ? "bg-orange-50 dark:bg-orange-500/10"
                                        : "hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                                }`}
                            >
                                {track.icon ? (
                                    <RepsIcon size={36} src={track.icon} />
                                ) : (
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-lg text-orange-500 dark:bg-orange-500/15">
                                        <SolarIcon name="code-square-bold-duotone" />
                                    </span>
                                )}
                                <span className="min-w-0 flex-1">
                                    <span className="block break-words text-sm font-semibold text-gray-900 dark:text-white">
                                        {track.title}
                                    </span>
                                    <span className="block text-xs text-gray-400">
                                        {done} / {total}
                                    </span>
                                </span>
                                {isCurrent ? (
                                    <SolarIcon
                                        name="check-circle-bold"
                                        className="text-lg text-orange-500"
                                    />
                                ) : null}
                            </Link>
                        );
                    })}

                    {/* Bientôt : l'univers grandit */}
                    <div className="mt-1 flex items-center gap-3 rounded-xl px-2.5 py-2 opacity-70">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-lg text-gray-400 dark:bg-white/10 dark:text-grayDark-500">
                            <SolarIcon name="stars-bold-duotone" />
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-gray-500 dark:text-grayDark-400">
                                {t("reps_catalog_soon_title")}
                            </span>
                            <span className="block break-words text-xs text-gray-400 dark:text-grayDark-500">
                                {t("reps_catalog_soon_desc")}
                            </span>
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
